import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ASSIGNED_ITEM_MARKING_TASK_OPTIONS,
  BLACKOUT_DOW_PROPS,
  BlackoutDowProp,
  IDisallowedTimeRange,
  IRuleScopePickerOptions,
  applyDowPreset,
  hasRuleScope,
  isExplicitAllDayRange,
  isOvernightRange,
  newRecurringRange,
  parseMarkingTasks,
  parseScopeIds,
  validateRange,
} from '../types';

/**
 * Single-rule configuration form — column 3 of the three-panel editor.
 */
@Component({
  selector: 'blackout-hours-rule-form',
  templateUrl: './blackout-hours-rule-form.component.html',
  styleUrls: ['./blackout-hours-rule-form.component.scss'],
})
export class BlackoutHoursRuleFormComponent {

  @Input() range: IDisallowedTimeRange;
  @Input() index: number = 0;
  @Input() timezone: string;
  @Input() disabled: boolean = false;
  @Input() dateSectionOpen: boolean = false;
  @Input() scopePickerOptions: IRuleScopePickerOptions | null = null;

  @Output() rangeChange = new EventEmitter<IDisallowedTimeRange>();
  @Output() dateSectionOpenChange = new EventEmitter<boolean>();

  BLACKOUT_DOW_PROPS = BLACKOUT_DOW_PROPS;
  markingTaskOptions = ASSIGNED_ITEM_MARKING_TASK_OPTIONS;
  isOvernightRange = isOvernightRange;
  validateRange = validateRange;
  scopeSectionOpen = false;

  private emit(next: IDisallowedTimeRange) {
    this.rangeChange.emit(next);
  }

  setRuleName(raw: string) {
    if (this.disabled) return;
    const name = raw != null ? String(raw).trim() : '';
    this.emit({ ...this.range, name: name || null });
  }

  isDowChecked(prop: BlackoutDowProp): boolean {
    return Number(this.range[prop]) === 1;
  }

  setDowChecked(prop: BlackoutDowProp, checked: boolean) {
    if (this.disabled) return;
    this.emit({ ...this.range, [prop]: checked ? 1 : 0 });
  }

  setWeekdayPreset(preset: 'weekdays' | 'weekend' | 'all' | 'none') {
    if (this.disabled) return;
    this.emit(applyDowPreset(this.range, preset));
  }

  isTaskChecked(taskType: string): boolean {
    return parseMarkingTasks(this.range.marking_tasks).includes(taskType);
  }

  toggleTask(taskType: string) {
    if (this.disabled) return;
    const tasks = parseMarkingTasks(this.range.marking_tasks);
    const idx = tasks.indexOf(taskType);
    if (idx === -1) tasks.push(taskType);
    else tasks.splice(idx, 1);
    this.emit({ ...this.range, marking_tasks: tasks });
  }

  /** True when the rule uses no clock span (full-day blackout). */
  isAllDayMode(): boolean {
    return isExplicitAllDayRange(this.range);
  }

  setAllDayMode(checked: boolean) {
    if (this.disabled) return;
    if (!checked) {
      const defaults = newRecurringRange();
      this.emit({
        ...this.range,
        is_allow_all_day: 0,
        time_start: defaults.time_start,
        time_end: defaults.time_end,
      });
      return;
    }
    this.emit({
      ...this.range,
      is_allow_all_day: 0,
      time_start: null,
      time_end: null,
    });
  }

  onTimeChange(field: 'time_start' | 'time_end', value: string) {
    if (this.disabled) return;
    this.emit({
      ...this.range,
      [field]: value,
      is_allow_all_day: 0,
    });
  }

  onDateStartChange(value: string | null) {
    if (this.disabled) return;
    this.dateSectionOpenChange.emit(true);
    this.emit({ ...this.range, date_start: value || null });
  }

  onDateEndChange(value: string | null) {
    if (this.disabled) return;
    this.dateSectionOpenChange.emit(true);
    this.emit({ ...this.range, date_end: value || null });
  }

  hasDateValues(): boolean {
    return !!(this.range?.date_start || this.range?.date_end);
  }

  openDateSection() {
    if (this.disabled) return;
    this.dateSectionOpenChange.emit(true);
  }

  hideDateSection() {
    this.dateSectionOpenChange.emit(false);
  }

  clearDates() {
    if (this.disabled) return;
    this.dateSectionOpenChange.emit(false);
    this.emit({ ...this.range, date_start: null, date_end: null });
  }

  hasScopeValues(): boolean {
    return hasRuleScope(this.range);
  }

  openScopeSection() {
    if (this.disabled) return;
    this.scopeSectionOpen = true;
  }

  hideScopeSection() {
    this.scopeSectionOpen = false;
  }

  clearScope() {
    if (this.disabled) return;
    this.scopeSectionOpen = false;
    this.emit({
      ...this.range,
      scope_batch_alloc_policy_ids: [],
      scope_marking_window_item_ids: [],
      scope_scorer_uids: [],
    });
  }

  private scopeField(
    field: 'scope_batch_alloc_policy_ids' | 'scope_marking_window_item_ids' | 'scope_scorer_uids',
  ): number[] {
    return parseScopeIds(this.range[field]);
  }

  isScopeChecked(
    field: 'scope_batch_alloc_policy_ids' | 'scope_marking_window_item_ids' | 'scope_scorer_uids',
    id: number,
  ): boolean {
    return this.scopeField(field).includes(Number(id));
  }

  toggleScope(
    field: 'scope_batch_alloc_policy_ids' | 'scope_marking_window_item_ids' | 'scope_scorer_uids',
    id: number,
  ) {
    if (this.disabled) return;
    this.scopeSectionOpen = true;
    const next = this.scopeField(field);
    const num = Number(id);
    const idx = next.indexOf(num);
    if (idx === -1) next.push(num);
    else next.splice(idx, 1);
    next.sort((a, b) => a - b);
    this.emit({ ...this.range, [field]: next });
  }

  policyLabel(row: { id: number; slug?: string | null }): string {
    const slug = row?.slug ? String(row.slug).trim() : '';
    return slug ? `#${row.id} ${slug}` : `#${row.id}`;
  }

  itemLabel(row: { id: number; caption?: string | null; slug?: string | null }): string {
    const label = (row?.caption || row?.slug || '').toString().trim();
    return label ? `#${row.id} ${label}` : `#${row.id}`;
  }

  scorerLabel(row: { uid: number; first_name?: string | null; last_name?: string | null }): string {
    const name = [row?.first_name, row?.last_name].filter(Boolean).join(' ').trim();
    return name ? `${name} (#${row.uid})` : `#${row.uid}`;
  }
}
